import React, { type ReactElement, useMemo, useRef, useState } from 'react';
import { useDetectScroll } from '@saviynt/common';
import {
  Box,
  Button,
  FooterActionBar,
  Icon,
  InlineMessage,
  InputField,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';

import { MIN_COMMENT_LENGTH } from '../../../constants';
import { useInvertedLocalize } from '../../../hooks';

import './SodOpenActionModal.css';

interface SodOpenActionModalProps {
  isOpen: boolean;
  isLoading: boolean;
  headerIcon: ReactElement;
  title: string;
  subtitle: string;
  handleSumbit: (comment: string) => void;
  handleModalClose: () => void;
  isDisabled?: boolean;
}

/**
 * SodOpenActionModal
 */
function SodOpenActionModal({
  isOpen,
  isDisabled,
  isLoading,
  headerIcon,
  title,
  subtitle,
  handleSumbit,
  handleModalClose,
}: SodOpenActionModalProps) {
  const l = useInvertedLocalize();
  const sectionRef = useRef(null);
  const { isScrolled: sectionRefIsScrolled }: { isScrolled: boolean } =
    useDetectScroll(null, sectionRef);

  const [commentValue, setCommentValue] = useState('');
  const [isCommentCritical, setIsCommentCritical] = useState(false);

  // Constants
  const isSubmitButtonEnable = useMemo(
    () => commentValue.trim().length >= MIN_COMMENT_LENGTH,
    [commentValue]
  );

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={handleModalClose}
      headerIcon={headerIcon}
      title={title}
      subtitle={subtitle}
      isCloseButtonVisible={!isDisabled}
      contentBody={
        <Box className='Sod-OpenActionModal-bodyWrapper'>
          <InputField
            name='move-to-open-action-comment'
            kind='multiline'
            label={
              <Typography kind='label' htmlFor='move-to-open-action-comment'>
                {l('Comments')}
              </Typography>
            }
            tabIndex={0}
            value={commentValue}
            setValue={setCommentValue}
            placeholder={l('Provide a reason for your decision')}
            isRequired={!isSubmitButtonEnable}
            isCritical={isCommentCritical}
            setIsCritical={setIsCommentCritical}
            CriticalHelperText={
              <InlineMessage
                text={
                  commentValue.trim().length < 1
                    ? l('This required field is incomplete')
                    : `Your comments must contain more than ${
                        MIN_COMMENT_LENGTH - 1
                      } characters.`
                }
                colorTheme='critical'
                size='small'
                leftIcon={<Icon kind='AlertCriticalFilled' />}
              />
            }
          />
        </Box>
      }
      FooterActionBarComp={
        <FooterActionBar size='large' isScrolled={sectionRefIsScrolled}>
          <div />
          <div className='Sod-OpenActionModal-footerActionButtons'>
            <Button
              type='button'
              kind='ghost'
              size='medium'
              isDisabled={isDisabled ?? isLoading}
              onClick={handleModalClose}>
              {l('Cancel')}
            </Button>
            <Button
              type='button'
              kind={isSubmitButtonEnable ? 'filled' : 'outlined'}
              size='medium'
              onClick={() => {
                if (isSubmitButtonEnable) {
                  handleSumbit(commentValue);

                  return;
                }

                setIsCommentCritical(true);
              }}
              isDisabled={isDisabled && !isLoading}
              isLoading={isLoading}
              loaderText={l('Saving')}>
              {l('Save')}
            </Button>
          </div>
        </FooterActionBar>
      }
    />
  );
}

SodOpenActionModal.defaultProps = {
  isDisabled: false,
};

export default SodOpenActionModal;
